import { Alert, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { reservationService } from '@yard/services/reservation.service'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { YardIssueDto } from '../../models/yard-issue.model'
import { YardIssueCard } from '../molecules/YardIssueCard'
import { YardIssueJourneyPicker } from '../molecules/YardIssueJourneyPicker'
import { YardIssueResolutionFormList } from '../organisms/YardIssueResolutionFormList'
import { YardIssueResolveDialogUIStore } from './yard-issue-resolve-dialog.ui-store'

export interface InternalWorkInstructionParams {
  containerJourneyId: number
  yardBlockId: string
  destination: Array<{ bay: number; row: number; tier: number }>
}

interface Props {
  issue: YardIssueDto | undefined
  onClose: () => void
  onCreateInternalWorkInstruction: (params: InternalWorkInstructionParams) => Promise<void>
}

export const YardIssueResolveDialog = observer(
  ({ issue, onClose, onCreateInternalWorkInstruction }: Props) => {
    const { t } = useTranslate()
    const uiStore = useMemo(() => new YardIssueResolveDialogUIStore(), [])

    useEffect(() => {
      uiStore.setIssue(issue)
    }, [issue, uiStore])

    const handleClose = () => {
      onClose()
      uiStore.reset()
    }

    const createInternalMove = async () => {
      if (!uiStore.selectedJourneyId || !uiStore.journeyPosition) return

      const reservation = await reservationService.create({
        journeyId: uiStore.selectedJourneyId,
        blockId: uiStore.journeyPosition.blockId,
        position:
          uiStore.journeyPosition.bay &&
          uiStore.journeyPosition.row &&
          uiStore.journeyPosition.tier
            ? {
                bay: uiStore.journeyPosition.bay,
                row: uiStore.journeyPosition.row,
                tier: uiStore.journeyPosition.tier,
              }
            : undefined,
      })

      const destination = reservation.positions.map(p => ({
        bay: p.bay,
        row: p.row,
        tier: p.tier,
      }))

      await onCreateInternalWorkInstruction({
        containerJourneyId: uiStore.selectedJourneyId,
        yardBlockId: uiStore.journeyPosition.blockId,
        destination,
      })
      handleClose()
    }

    const handleConfirm = async () => {
      switch (uiStore.selectedAction) {
        case 'createInternalMove':
          return createInternalMove()
      }
    }

    return (
      <ConfirmationDialog
        open={!!issue}
        title={t('resolveYardIssue', 'Resolve yard issue')}
        primaryActionText={t('confirm', 'Confirm')}
        primaryActionDisabled={uiStore.confirmDisabled}
        onConfirm={handleConfirm}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='lg'
        onClose={handleClose}
        hasBorderTop
      >
        {issue ? (
          <Stack gap={2}>
            <YardIssueCard issue={issue} />

            <Alert severity='info'>
              {uiStore.hasResolutionAction
                ? t(
                    'yardIssueResolutionGuidance',
                    'This issue can be resolved by creating a housekeeping / internal move from this dialog, or outside this dialog by administratively updating the container position.',
                  )
                : t(
                    'yardIssueResolutionGuidanceManualOnly',
                    'This issue can be resolved outside this dialog by creating a housekeeping / internal move or administratively updating the container position.',
                  )}
            </Alert>

            {uiStore.shouldShowJourneyPicker && (
              <YardIssueJourneyPicker
                issue={issue}
                selectedJourneyId={uiStore.selectedJourneyId}
                onSelect={journeyId => uiStore.setSelectedJourneyId(journeyId)}
              />
            )}

            {uiStore.selectedJourneyId && uiStore.hasResolutionAction && (
              <YardIssueResolutionFormList
                key={`${issue.issueId}-${uiStore.selectedJourneyId}`}
                availableActions={uiStore.availableActions}
                defaultAction={uiStore.defaultAction}
                targetJourneyId={uiStore.selectedJourneyId}
                onActionChange={action => uiStore.setSelectedAction(action)}
                onPositionChange={position => uiStore.setJourneyPosition(position)}
                onPositionBlockChange={isBlocked => uiStore.setPositionBlocked(isBlocked)}
              />
            )}
          </Stack>
        ) : (
          <Typography>{t('issueNotFound', 'Issue not found')}</Typography>
        )}
      </ConfirmationDialog>
    )
  },
)
